import * as zod from 'zod';

/**
 * Shapes for the `roboflowWorkflows` feathers service — a read-only passthrough
 * to Roboflow, authenticated on the robot with the stored workspace API key.
 *
 * It exists because Roboflow's schema endpoint (`describe_interface`) lives on
 * the inference server and authenticates with that long-lived workspace key,
 * which deliberately never reaches the browser (see `roboflowCredentialsHooks`).
 * The OAuth access token the browser does hold is a different credential and is
 * rejected there, so the robot — which has the key — makes the call instead.
 *
 * `find` lists the workspace's workflows; `get` describes one of them.
 */
export const RoboflowWorkflowSummary = zod.object({
  /** Path segment used to run and describe the workflow. */
  id: zod.string(),
  name: zod.string(),
});

export type RoboflowWorkflowSummary = zod.infer<typeof RoboflowWorkflowSummary>;

export const RoboflowWorkflowListData = zod.object({
  /** Workspace the stored key belongs to — the one these workflows came from. */
  workspaceName: zod.string().optional(),
  workflows: zod.array(RoboflowWorkflowSummary),
});

export type RoboflowWorkflowListData = zod.infer<
  typeof RoboflowWorkflowListData
>;

/**
 * One workflow's interface, as `describe_interface` reports it.
 *
 * `outputs` maps an output name to its kinds. A value is normally a list of kind
 * strings, but a compound output nests another object of the same shape — so
 * anything reading kinds has to recurse rather than flatten one level.
 */
export const RoboflowWorkflowInterfaceData = zod.object({
  id: zod.string(),
  inputs: zod.record(zod.unknown()),
  outputs: zod.record(zod.unknown()),
});

export type RoboflowWorkflowInterfaceData = zod.infer<
  typeof RoboflowWorkflowInterfaceData
>;

/** Union of what the service can return, for the feathers client typing. */
export type RoboflowWorkflowsData =
  | RoboflowWorkflowListData
  | RoboflowWorkflowInterfaceData;
